/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global Excel console */

/**
 * Name of the sheet {@link ExcelService.createCompletionCertificate} writes
 * into. Exported so the Review-page "worksheet has no status item" warning
 * and the sync-worksheets service can filter it out — the certificate is
 * WFI-generated and shouldn't be treated as a workpaper missing a status
 * item just because a manager already signed off.
 */
export const COMPLETION_CERTIFICATE_SHEET_NAME = "Completion Certificate";

/**
 * Thrown by callers that want to signal "you tried to do something Excel-only
 * in a host where Excel isn't available." ExcelService itself doesn't throw
 * this today — callers gate on `useWorkflowIntelligence().isStandalone` and
 * skip the call entirely. Exposed here so callers can use one shared error
 * type if they later want to defensively throw at the ExcelService boundary.
 */
export class NotInExcelError extends Error {
  constructor(method: string) {
    super(`Excel is not available in the current host — ${method} cannot run.`);
    this.name = "NotInExcelError";
  }
}

/**
 * Thrown when the workbook is open read-only, so a write can't land where
 * the user expects. Worth its own type because the failure mode is
 * confusing otherwise: Excel may fork the document into a separate copy
 * (desktop Save As, or Excel-for-web's "editing copy"), and the user ends
 * up looking at a different workbook than the one they were working in.
 * Callers should surface this as an actionable message rather than a
 * generic failure.
 */
export class WorkbookReadOnlyError extends Error {
  constructor(operation: string) {
    super(
      `This workbook is open in read-only mode, so ${operation} can't be written to it. ` +
        `Close and reopen the workbook with edit access (check whether someone else has it ` +
        `open, whether it came from an email attachment, or whether it's marked as Final), ` +
        `then try again.`,
    );
    this.name = "WorkbookReadOnlyError";
  }
}

/**
 * Thrown when the writes succeeded but persisting the workbook failed.
 * Distinct from a write failure — the user's data IS in the in-memory
 * workbook and will survive a manual Ctrl+S, so the message should say
 * "save it yourself" rather than "it didn't work".
 */
export class WorkbookSaveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "WorkbookSaveError";
  }
}

/**
 * Thrown when the workbook has never been saved to a real location.
 * `Workbook.save()` on such a workbook writes it "with default name in
 * the default location" per the Office.js SaveBehavior docs — producing
 * a file the user never chose, in a folder they won't think to look in.
 * For anything the user needs to find again (a signed-off workpaper,
 * most of all) that's worse than refusing.
 */
export class WorkbookNotSavedError extends Error {
  constructor(operation: string) {
    super(
      `This workbook hasn't been saved anywhere yet, so ${operation} can't be stored reliably. ` +
        `Save the workbook (Ctrl+S) to a proper location first, then try again.`,
    );
    this.name = "WorkbookNotSavedError";
  }
}

/**
 * Excel Service - Handles all Excel-specific operations
 * This service provides methods for interacting with Excel workbooks, worksheets, and ranges
 */

export class ExcelService {
  /**
   * Set the value of a specific cell
   * @param cellAddress - The cell address (e.g., "A1", "B2")
   * @param value - The value to set (string, number, boolean, etc.)
   * @param worksheetName - Optional worksheet name. If not provided, uses the active worksheet
   */
  static async setCellValue(cellAddress: string, value: any, worksheetName?: string): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = worksheetName
          ? context.workbook.worksheets.getItem(worksheetName)
          : context.workbook.worksheets.getActiveWorksheet();
        const range = sheet.getRange(cellAddress);
        // Route strings with a leading `=` through range.formulas so they
        // evaluate as formulas. range.values would store them as literal
        // text — the specific mode that caused the C1 bank-rec agent to
        // silently write ~12 formula strings as text on inserted rows.
        if (typeof value === "string" && value.startsWith("=")) {
          range.formulas = [[value]];
        } else {
          range.values = [[value]];
        }
        // Workpapers have pre-defined column widths — never autofit (would resize
        // them) and enable text wrapping so long values stay readable within
        // their existing column width.
        range.format.wrapText = true;
        await context.sync();
      });
    } catch (error) {
      console.error("Error setting cell value in Excel:", error);
      throw new Error(`Failed to set value in cell ${cellAddress}: ${error}`);
    }
  }

  /**
   * Write a formula to a single cell (e.g. =HYPERLINK(...)).
   * Use this for formula-based changes; setCellValue writes plain values which
   * would turn a HYPERLINK string into a literal text cell.
   *
   * Note: deliberately does NOT autofit columns. Office.js' autofit measures the
   * formula source text (which can be very long for HYPERLINK with full URLs),
   * not the displayed value, so it would balloon the column width for short
   * displayed text. Users typically already have a planned layout when inserting
   * formulas.
   */
  static async setCellFormula(cellAddress: string, formula: string, worksheetName?: string): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = worksheetName
          ? context.workbook.worksheets.getItem(worksheetName)
          : context.workbook.worksheets.getActiveWorksheet();
        const range = sheet.getRange(cellAddress);
        range.formulas = [[formula]];
        await context.sync();
      });
    } catch (error) {
      console.error("Error setting cell formula in Excel:", error);
      throw new Error(`Failed to set formula in cell ${cellAddress}: ${error}`);
    }
  }

  /**
   * Write a native Excel hyperlink to a cell.
   *
   * Prefer this over an =HYPERLINK() formula when the URL contains a `#`
   * fragment (e.g. deep-links to a PDF page like `.../foo.pdf#page=6`).
   * The HYPERLINK formula's link_location argument interprets `#` as an
   * internal-reference delimiter — it splits the URL at the `#` and tries
   * to resolve everything after it as a named range or sheet, which
   * throws when the formula is applied via Office.js. Native hyperlinks
   * take the URL as-is and preserve the fragment when the browser opens
   * the link.
   */
  static async setCellHyperlink(cellAddress: string, url: string, displayName: string, worksheetName?: string): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = worksheetName
          ? context.workbook.worksheets.getItem(worksheetName)
          : context.workbook.worksheets.getActiveWorksheet();
        const range = sheet.getRange(cellAddress);
        range.hyperlink = {
          address: url,
          textToDisplay: displayName,
        };
        await context.sync();
      });
    } catch (error) {
      console.error("Error setting cell hyperlink in Excel:", error);
      throw new Error(`Failed to set hyperlink in cell ${cellAddress}: ${error}`);
    }
  }

  /**
   * Insert data into a range of cells
   * @param data - 2D array of data to insert
   * @param startCell - Starting cell address (e.g., "A1")
   */
  static async insertRange(data: (string | number)[][], startCell: string = "A1", worksheetName?: string): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = worksheetName
          ? context.workbook.worksheets.getItem(worksheetName)
          : context.workbook.worksheets.getActiveWorksheet();

        // Normalize jagged arrays: pad all rows to the max column count
        const maxCols = data.reduce((max, row) => Math.max(max, row.length), 0);
        const normalized = data.map((row) => {
          if (row.length < maxCols) {
            return [...row, ...Array(maxCols - row.length).fill("")];
          }
          return row;
        });

        const range = sheet.getRange(startCell).getResizedRange(normalized.length - 1, maxCols - 1);
        // If any cell in the batch has a leading `=`, use range.formulas so
        // formulas evaluate instead of being stored as literal text.
        // range.formulas handles plain-value cells correctly too (strings
        // without `=` are just written as values), so it's safe to route the
        // whole batch through it when any formula is present.
        const hasFormula = normalized.some(row =>
          row.some(cell => typeof cell === "string" && cell.startsWith("=")),
        );
        if (hasFormula) {
          range.formulas = normalized;
        } else {
          range.values = normalized;
        }
        // Workpapers have pre-defined column widths — never autofit (would
        // resize them) and enable text wrapping so long values stay readable.
        range.format.wrapText = true;
        await context.sync();
      });
    } catch (error) {
      console.error("Error inserting range into Excel:", error);
      throw new Error(`Failed to insert range starting at ${startCell}: ${error}`);
    }
  }

  /**
   * Whether the workbook can be written to and saved in place.
   *
   * `readOnly` — the workbook is open read-only (locked by another user on
   * SharePoint, opened from an email attachment, marked Final, read-only
   * share, or view-only in Excel for the web). Writes will fail, and a
   * `save()` can fork the document into a separate copy.
   *
   * `previouslySaved` — false means the workbook has never been saved to a
   * real location. `Workbook.save()` on such a workbook silently writes it
   * "with default name in the default location" (per the Office.js
   * SaveBehavior docs) — i.e. it produces a file the user never chose,
   * which presents as a mystery workbook appearing out of nowhere.
   *
   * Both properties need ExcelApi 1.8; we feature-detect rather than
   * hard-fail so an older Excel build degrades to today's behaviour
   * instead of breaking sign-off entirely.
   */
  static async getWorkbookWriteState(): Promise<{
    readOnly: boolean;
    previouslySaved: boolean;
    supported: boolean;
  }> {
    const supported =
      typeof Office !== "undefined" &&
      !!Office.context?.requirements?.isSetSupported("ExcelApi", "1.8");
    if (!supported) {
      // Unknown — report writable so callers behave exactly as they did
      // before this check existed.
      return { readOnly: false, previouslySaved: true, supported: false };
    }
    try {
      return await Excel.run(async (context) => {
        const workbook = context.workbook;
        workbook.load(["readOnly", "previouslySaved"]);
        await context.sync();
        return {
          readOnly: workbook.readOnly,
          previouslySaved: workbook.previouslySaved,
          supported: true,
        };
      });
    } catch (error) {
      // Don't let a diagnostic read break the caller's real work.
      console.warn("Could not read workbook write state:", error);
      return { readOnly: false, previouslySaved: true, supported: false };
    }
  }

  /**
   * Guard for any operation that writes to the workbook AND needs those
   * writes to survive. Throws before the caller touches anything, so a
   * failure is a clean refusal rather than a half-applied change.
   *
   * Two blocking conditions:
   *   * read-only  — writes can't land where the user expects, and Excel
   *                  may fork the document into a separate copy (this is
   *                  what caused the "certificate appeared in a different
   *                  workbook" incident).
   *   * never saved — `save()` would dump the workbook to a default name
   *                  in a default location.
   *
   * No-ops when the Office API set is too old to tell (see
   * {@link getWorkbookWriteState}) — we'd rather behave as before than
   * block sign-off on a build we can't inspect.
   *
   * @param operation Human-readable noun phrase for the error message,
   *                  e.g. "the completion certificate" / "the sign-off".
   */
  static async assertWorkbookWritable(operation: string): Promise<void> {
    const state = await ExcelService.getWorkbookWriteState();
    if (!state.supported) return;
    if (state.readOnly) throw new WorkbookReadOnlyError(operation);
    if (!state.previouslySaved) throw new WorkbookNotSavedError(operation);
  }

  /**
   * Persist the workbook to disk / OneDrive. Call after a batch of writes to
   * commit the changes (otherwise pending changes only live in the in-memory
   * workbook until Excel auto-saves).
   *
   * Passes SaveBehavior.save explicitly (it's the default, but being
   * explicit documents the intent: never pop a Save As dialog at the user
   * mid-flow). Note the Office.js caveat — on a workbook that has never
   * been saved, this writes it with a default name to a default location.
   * Callers that care should pre-flight with {@link getWorkbookWriteState}.
   *
   * Throws a `WorkbookSaveError` so callers can distinguish "the writes
   * landed but persisting them failed" from "the writes themselves failed"
   * — the two need very different messages in front of a user.
   */
  static async save(): Promise<void> {
    try {
      await Excel.run(async (context) => {
        context.workbook.save(Excel.SaveBehavior.save);
        await context.sync();
      });
    } catch (error) {
      console.error("Error saving workbook:", error);
      throw new WorkbookSaveError(`Failed to save workbook: ${error}`);
    }
  }

  /**
   * Set formulas into a range of cells
   * @param formulas - 2D array of formula strings (e.g., "=A1+B1") or empty strings
   * @param startCell - Starting cell address (e.g., "O2")
   * @param worksheetName - Optional worksheet name. If not provided, uses the active worksheet
   */
  static async setFormulas(formulas: string[][], startCell: string = "A1", worksheetName?: string): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = worksheetName
          ? context.workbook.worksheets.getItem(worksheetName)
          : context.workbook.worksheets.getActiveWorksheet();

        const maxCols = formulas.reduce((max, row) => Math.max(max, row.length), 0);
        const normalized = formulas.map((row) => {
          if (row.length < maxCols) {
            return [...row, ...Array(maxCols - row.length).fill("")];
          }
          return row;
        });

        const range = sheet.getRange(startCell).getResizedRange(normalized.length - 1, maxCols - 1);
        range.formulas = normalized;
        await context.sync();
      });
    } catch (error) {
      console.error("Error setting formulas in Excel:", error);
      throw new Error(`Failed to set formulas starting at ${startCell}: ${error}`);
    }
  }

  /**
   * Get the value from a specific cell
   * @param cellAddress - The cell address to read from
   * @param worksheetName - Optional worksheet name. If not provided, uses the active worksheet
   * @returns The cell value
   */
  static async getCellValue(cellAddress: string, worksheetName?: string): Promise<any> {
    try {
      return await Excel.run(async (context) => {
        const sheet = worksheetName
          ? context.workbook.worksheets.getItem(worksheetName)
          : context.workbook.worksheets.getActiveWorksheet();
        const range = sheet.getRange(cellAddress);
        range.load("values");
        await context.sync();
        return range.values[0][0];
      });
    } catch (error) {
      console.error("Error reading cell value from Excel:", error);
      throw new Error(`Failed to read cell ${cellAddress}: ${error}`);
    }
  }

  /**
   * Get values from a range of cells
   * @param rangeAddress - The range address (e.g., "A1:C3")
   * @param worksheetName - Optional worksheet name. If not provided, uses the active worksheet
   * @returns 2D array of cell values
   */
  static async getRangeValues(rangeAddress: string, worksheetName?: string): Promise<any[][]> {
    try {
      return await Excel.run(async (context) => {
        const sheet = worksheetName
          ? context.workbook.worksheets.getItem(worksheetName)
          : context.workbook.worksheets.getActiveWorksheet();
        const range = sheet.getRange(rangeAddress);
        range.load("values");
        await context.sync();
        return range.values;
      });
    } catch (error) {
      console.error("Error reading range values from Excel:", error);
      throw new Error(`Failed to read range ${rangeAddress}: ${error}`);
    }
  }

  /**
   * Clear ALL contents and formatting from a worksheet's used range.
   * Use before re-importing data into a sheet that may already hold stale rows
   * from a previous import — `insertRange` only overwrites the cells it writes
   * to, leaving any rows beyond the new data intact.
   *
   * Uses `getUsedRangeOrNullObject` so a brand-new (empty) sheet doesn't throw.
   */
  static async clearWorksheetContents(worksheetName: string): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(worksheetName);
        const usedRange = sheet.getUsedRangeOrNullObject();
        usedRange.load("isNullObject");
        await context.sync();
        if (!usedRange.isNullObject) {
          usedRange.clear(Excel.ClearApplyTo.all);
          await context.sync();
        }
      });
    } catch (error) {
      console.error("Error clearing worksheet contents:", error);
      throw new Error(`Failed to clear worksheet '${worksheetName}': ${error}`);
    }
  }

  /**
   * Clear content from a specific cell or range
   * @param cellOrRangeAddress - The cell or range address to clear
   */
  static async clearContent(cellOrRangeAddress: string, worksheetName?: string): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = worksheetName
          ? context.workbook.worksheets.getItem(worksheetName)
          : context.workbook.worksheets.getActiveWorksheet();
        const range = sheet.getRange(cellOrRangeAddress);
        range.clear(Excel.ClearApplyTo.contents);
        await context.sync();
      });
    } catch (error) {
      console.error("Error clearing content in Excel:", error);
      throw new Error(`Failed to clear content in ${cellOrRangeAddress}: ${error}`);
    }
  }

  /**
   * Create a new blank worksheet. Idempotent — if a sheet with the same name
   * already exists, this is a no-op. Used by Incoming Changes to apply
   * `create_worksheet` cell changes; the reviewer may have manually created
   * a sheet with the same name before accepting the change, and that shouldn't
   * error out.
   */
  static async createWorksheet(sheetName: string): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const existing = context.workbook.worksheets.getItemOrNullObject(sheetName);
        await context.sync();
        if (!existing.isNullObject) return;
        context.workbook.worksheets.add(sheetName);
        await context.sync();
      });
    } catch (error) {
      console.error("Error creating worksheet in Excel:", error);
      throw new Error(`Failed to create worksheet '${sheetName}': ${error}`);
    }
  }

  /**
   * Insert `count` blank rows into the given worksheet starting at rowNumber
   * (1-based). Existing rows at and below shift down. Constructs a range
   * spanning the correct number of rows so Excel's insert-entire-row
   * operation inserts exactly that many.
   */
  static async insertRows(sheetName: string, rowNumber: number, count: number): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetName);
        const rangeAddress = count === 1
          ? `A${rowNumber}`
          : `A${rowNumber}:A${rowNumber + count - 1}`;
        sheet.getRange(rangeAddress).getEntireRow().insert(Excel.InsertShiftDirection.down);
        await context.sync();
      });
    } catch (error) {
      console.error("Error inserting rows in Excel:", error);
      throw new Error(`Failed to insert ${count} row(s) at ${sheetName}!${rowNumber}: ${error}`);
    }
  }

  /**
   * Insert `count` blank columns starting at columnLetter (A, B, ..., AA...).
   * Existing columns at and after shift right.
   */
  static async insertColumns(sheetName: string, columnLetter: string, count: number): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetName);
        // For count > 1 we need a range that spans the required column
        // count; ExcelService can just resize from the anchor column.
        const anchor = sheet.getRange(`${columnLetter}1`);
        const range = count === 1 ? anchor : anchor.getResizedRange(0, count - 1);
        range.getEntireColumn().insert(Excel.InsertShiftDirection.right);
        await context.sync();
      });
    } catch (error) {
      console.error("Error inserting columns in Excel:", error);
      throw new Error(`Failed to insert ${count} column(s) at ${sheetName}!${columnLetter}: ${error}`);
    }
  }

  /**
   * Apply cell formatting to a range. Every option is optional; only the
   * options provided get applied — existing formatting on other properties
   * stays intact.
   *
   * Wire format is the same shape the API's set_formatting tool accepts,
   * so this can be called with the raw JSON.parse of a CellChange value.
   */
  static async applyFormatting(
    sheetName: string,
    rangeAddress: string,
    options: {
      bold?: boolean;
      italic?: boolean;
      underline?: boolean;
      fontColor?: string;
      fillColor?: string;
      fontSize?: number;
      numberFormat?: string;
      horizontalAlignment?: string;
    }
  ): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetName);
        const range = sheet.getRange(rangeAddress);
        if (options.bold !== undefined) range.format.font.bold = options.bold;
        if (options.italic !== undefined) range.format.font.italic = options.italic;
        if (options.underline !== undefined) {
          range.format.font.underline = options.underline
            ? Excel.RangeUnderlineStyle.single
            : Excel.RangeUnderlineStyle.none;
        }
        if (options.fontColor) range.format.font.color = options.fontColor;
        if (options.fillColor) range.format.fill.color = options.fillColor;
        if (options.fontSize) range.format.font.size = options.fontSize;
        if (options.numberFormat) {
          range.numberFormat = [[options.numberFormat]];
        }
        if (options.horizontalAlignment) {
          const map: Record<string, Excel.HorizontalAlignment> = {
            left: Excel.HorizontalAlignment.left,
            center: Excel.HorizontalAlignment.center,
            centre: Excel.HorizontalAlignment.center,
            right: Excel.HorizontalAlignment.right,
          };
          const key = options.horizontalAlignment.toLowerCase();
          if (map[key]) range.format.horizontalAlignment = map[key];
        }
        await context.sync();
      });
    } catch (error) {
      console.error("Error applying formatting in Excel:", error);
      throw new Error(`Failed to apply formatting to ${sheetName}!${rangeAddress}: ${error}`);
    }
  }

  /**
   * Set an explicit column width (Excel column-width units, roughly
   * "characters of the default font" — 8.43 is Excel's default). Applies to
   * every column the range spans, so "A:A", "A:C", or a cell range like
   * "A1:C10" all work. `range.format.columnWidth = X` sets every intersected
   * column to the same width.
   */
  static async setColumnWidth(sheetName: string, rangeAddress: string, width: number): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetName);
        sheet.getRange(rangeAddress).format.columnWidth = width;
        await context.sync();
      });
    } catch (error) {
      console.error("Error setting column width in Excel:", error);
      throw new Error(`Failed to set column width on ${sheetName}!${rangeAddress}: ${error}`);
    }
  }

  /**
   * Autofit every column the range spans to the widest cell it contains.
   *
   * Bracket the autofit with a wrapText toggle — with wrap on (our default
   * for all writes), Office.js measures the wrapped width, which collapses
   * to the longest single word and gives an unusably thin column. We turn
   * wrap off for the measurement, then re-enable it so the workpaper's
   * wrap-on convention still holds afterwards.
   */
  static async autofitColumns(sheetName: string, rangeAddress: string): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetName);
        const range = sheet.getRange(rangeAddress);
        range.format.wrapText = false;
        await context.sync();
        range.format.autofitColumns();
        range.format.wrapText = true;
        await context.sync();
      });
    } catch (error) {
      console.error("Error autofitting columns in Excel:", error);
      throw new Error(`Failed to autofit columns on ${sheetName}!${rangeAddress}: ${error}`);
    }
  }

  /**
   * Set an explicit row height (points; 72 pt = 1 inch, Excel default is
   * 15). Every row the range spans is set to the same height.
   */
  static async setRowHeight(sheetName: string, rangeAddress: string, height: number): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetName);
        sheet.getRange(rangeAddress).format.rowHeight = height;
        await context.sync();
      });
    } catch (error) {
      console.error("Error setting row height in Excel:", error);
      throw new Error(`Failed to set row height on ${sheetName}!${rangeAddress}: ${error}`);
    }
  }

  /**
   * Autofit every row the range spans to the tallest cell it contains.
   * Especially useful after multi-line writes into cells with wrapText —
   * cells with wrapped content don't grow vertically on their own.
   */
  static async autofitRows(sheetName: string, rangeAddress: string): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetName);
        sheet.getRange(rangeAddress).format.autofitRows();
        await context.sync();
      });
    } catch (error) {
      console.error("Error autofitting rows in Excel:", error);
      throw new Error(`Failed to autofit rows on ${sheetName}!${rangeAddress}: ${error}`);
    }
  }

  /**
   * Format a range with basic styling
   * @param rangeAddress - The range to format
   * @param format - Formatting options
   */
  static async formatRange(
    rangeAddress: string,
    format: {
      bold?: boolean;
      italic?: boolean;
      fontSize?: number;
      fontColor?: string;
      backgroundColor?: string;
    },
    worksheetName?: string
  ): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = worksheetName
          ? context.workbook.worksheets.getItem(worksheetName)
          : context.workbook.worksheets.getActiveWorksheet();
        const range = sheet.getRange(rangeAddress);
        
        if (format.bold !== undefined) {
          range.format.font.bold = format.bold;
        }
        if (format.italic !== undefined) {
          range.format.font.italic = format.italic;
        }
        if (format.fontSize) {
          range.format.font.size = format.fontSize;
        }
        if (format.fontColor) {
          range.format.font.color = format.fontColor;
        }
        if (format.backgroundColor) {
          range.format.fill.color = format.backgroundColor;
        }
        
        await context.sync();
      });
    } catch (error) {
      console.error("Error formatting range in Excel:", error);
      throw new Error(`Failed to format range ${rangeAddress}: ${error}`);
    }
  }

  /**
   * Get a worksheet by name
   * @param name - Name of the worksheet to get
   * @returns The worksheet if it exists
   * @throws Error if the worksheet doesn't exist
   */
  static async getWorksheet(name: string): Promise<Excel.Worksheet> {
    try {
      return await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(name);
        sheet.load("name");
        await context.sync();
        return sheet;
      });
    } catch (error) {
      console.error("Error getting worksheet from Excel:", error);
      throw new Error(`Failed to get worksheet '${name}': ${error}`);
    }
  }

  /**
   * Get or create a worksheet by name
   * @param name - Name of the worksheet to get or create
   * @param visibility - Optional visibility setting for the worksheet if created. Defaults to "Visible"
   *                     Options: "Visible", "Hidden", "VeryHidden"
   * @returns The worksheet
   */
  static async getOrCreateWorksheet(
    name: string,
    visibility: Excel.SheetVisibility | "Visible" | "Hidden" | "VeryHidden" = Excel.SheetVisibility.visible
  ): Promise<Excel.Worksheet> {
    try {
      return await Excel.run(async (context) => {
        const sheets = context.workbook.worksheets;
        let worksheet: Excel.Worksheet;

        try {
          // Try to get existing worksheet
          worksheet = sheets.getItem(name);
          worksheet.load("name");
          await context.sync();
        } catch {
          // Worksheet doesn't exist, create it
          worksheet = sheets.add(name);
          worksheet.visibility = visibility;
          await context.sync();
        }

        return worksheet;
      });
    } catch (error) {
      console.error("Error getting or creating worksheet in Excel:", error);
      throw new Error(`Failed to get or create worksheet '${name}': ${error}`);
    }
  }

  /**
   * Get the name of the active worksheet
   * @returns The active worksheet name
   */
  static async getActiveWorksheetName(): Promise<string> {
    try {
      return await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load("name");
        await context.sync();
        return sheet.name;
      });
    } catch (error) {
      console.error("Error getting active worksheet name:", error);
      throw new Error(`Failed to get active worksheet name: ${error}`);
    }
  }

  /**
   * Get all visible worksheet names in the workbook
   * @returns Array of all visible worksheet names (excludes hidden and very hidden sheets)
   */
  static async getAllWorksheetNames(): Promise<string[]> {
    try {
      return await Excel.run(async (context) => {
        const sheets = context.workbook.worksheets;
        sheets.load("items/name, items/visibility");
        await context.sync();
        return sheets.items
          .filter((sheet) => sheet.visibility === Excel.SheetVisibility.visible)
          .map((sheet) => sheet.name);
      });
    } catch (error) {
      console.error("Error getting all worksheet names:", error);
      throw new Error(`Failed to get all worksheet names: ${error}`);
    }
  }

  /**
   * Get the ID and name of the active worksheet
   * The ID is a persistent GUID that remains constant even when the sheet is renamed
   * @returns Object with id (persistent GUID) and name (display name)
   */
  static async getActiveWorksheetInfo(): Promise<{ id: string; name: string }> {
    try {
      return await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        sheet.load("id, name");
        await context.sync();
        return { id: sheet.id, name: sheet.name };
      });
    } catch (error) {
      console.error("Error getting active worksheet info:", error);
      throw new Error(`Failed to get active worksheet info: ${error}`);
    }
  }

  /**
   * Get all visible worksheets with their IDs and names
   * The ID is a persistent GUID that remains constant even when the sheet is renamed
   * @returns Array of objects with id (persistent GUID) and name (display name)
   */
  static async getAllWorksheetsInfo(): Promise<{ id: string; name: string }[]> {
    try {
      return await Excel.run(async (context) => {
        const sheets = context.workbook.worksheets;
        sheets.load("items/id, items/name, items/visibility");
        await context.sync();
        return sheets.items
          .filter((sheet) => sheet.visibility === Excel.SheetVisibility.visible)
          .map((sheet) => ({ id: sheet.id, name: sheet.name }));
      });
    } catch (error) {
      console.error("Error getting all worksheets info:", error);
      throw new Error(`Failed to get all worksheets info: ${error}`);
    }
  }

  /**
   * Get the current name of a worksheet by its persistent ID
   * @param sheetId - The persistent worksheet ID (GUID)
   * @returns The current display name of the worksheet
   */
  static async getWorksheetNameById(sheetId: string): Promise<string> {
    try {
      return await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetId);
        sheet.load("name");
        await context.sync();
        return sheet.name;
      });
    } catch (error) {
      console.error("Error getting worksheet name by ID:", error);
      throw new Error(`Failed to get worksheet name for ID ${sheetId}: ${error}`);
    }
  }

  /**
   * Select a cell in a worksheet identified by its persistent ID
   * @param sheetId - The persistent worksheet ID (GUID)
   * @param cellAddress - The cell address (e.g., "A1", "B2:C5"). Defaults to "A1"
   */
  static async selectCellById(sheetId: string, cellAddress: string = "A1"): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getItem(sheetId);
        sheet.activate();
        const range = sheet.getRange(cellAddress);
        range.select();
        await context.sync();
      });
    } catch (error) {
      console.error("Error selecting cell by sheet ID:", error);
      throw new Error(`Failed to select cell ${cellAddress} in sheet ${sheetId}: ${error}`);
    }
  }

  /**
   * Get the name of the workbook
   * @returns The workbook filename (e.g., "MyWorkbook.xlsx")
   */
  static async getWorkbookName(): Promise<string> {
    try {
      return await Excel.run(async (context) => {
        const workbook = context.workbook;
        workbook.load("name");
        await context.sync();
        return workbook.name;
      });
    } catch (error) {
      console.error("Error getting workbook name:", error);
      throw new Error(`Failed to get workbook name: ${error}`);
    }
  }

  /**
   * Select a specific cell or range
   * @param cellAddress - The cell address (e.g., "A1", "B2:C5", "Sheet1!A1")
   */
  static async selectCell(cellAddress: string): Promise<void> {
    try {
      await Excel.run(async (context) => {
        // Parse sheet name if included (e.g., "Sheet1!A1")
        let worksheetName: string | undefined;
        let address = cellAddress;

        if (cellAddress.includes("!")) {
          const parts = cellAddress.split("!");
          worksheetName = parts[0];
          address = parts[1];
        }

        const sheet = worksheetName
          ? context.workbook.worksheets.getItem(worksheetName)
          : context.workbook.worksheets.getActiveWorksheet();

        // Activate the sheet first if navigating to a different sheet
        if (worksheetName) {
          sheet.activate();
        }

        const range = sheet.getRange(address);
        range.select();
        await context.sync();
      });
    } catch (error) {
      console.error("Error selecting cell in Excel:", error);
      throw new Error(`Failed to select cell ${cellAddress}: ${error}`);
    }
  }

  /**
   * Get the currently selected cell or range with sheet name
   * @returns Object with address (e.g., "A1"), sheet name, and full reference (e.g., "Sheet1!A1")
   */
  static async getSelectedCell(): Promise<{ address: string; sheet: string; fullReference: string }> {
    try {
      return await Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange();
        const sheet = range.worksheet;

        range.load("address");
        sheet.load("name");
        await context.sync();

        // Remove sheet name from address if it's included
        const address = range.address.includes("!")
          ? range.address.split("!")[1]
          : range.address;

        return {
          address,
          sheet: sheet.name,
          fullReference: `${sheet.name}!${address}`,
        };
      });
    } catch (error) {
      console.error("Error getting selected cell:", error);
      throw new Error(`Failed to get selected cell: ${error}`);
    }
  }

  /**
   * Register a callback for when the active worksheet changes
   * @param callback - Function to call when worksheet is activated, receives the worksheet name
   * @returns A function to unregister the event handler
   */
  static async onWorksheetActivated(
    callback: (worksheetName: string) => void
  ): Promise<() => Promise<void>> {
    let eventResult: OfficeExtension.EventHandlerResult<Excel.WorksheetActivatedEventArgs> | null = null;

    try {
      await Excel.run(async (context) => {
        const worksheets = context.workbook.worksheets;

        eventResult = worksheets.onActivated.add(async (event) => {
          try {
            // Get the worksheet name from the event
            await Excel.run(async (ctx) => {
              const worksheet = ctx.workbook.worksheets.getItem(event.worksheetId);
              worksheet.load("name");
              await ctx.sync();
              callback(worksheet.name);
            });
          } catch (error) {
            console.error("Error in worksheet activated handler:", error);
          }
        });

        await context.sync();
      });

      // Return cleanup function
      return async () => {
        if (eventResult) {
          await Excel.run(async (context) => {
            eventResult!.remove();
            await context.sync();
          });
        }
      };
    } catch (error) {
      console.error("Error registering worksheet activated handler:", error);
      throw new Error(`Failed to register worksheet activated handler: ${error}`);
    }
  }

  /**
   * Register a callback for when a new worksheet is added to the workbook
   * @param callback - Function to call when a worksheet is added, receives the worksheet ID
   * @returns A function to unregister the event handler
   */
  static async onWorksheetAdded(
    callback: (worksheetId: string) => void
  ): Promise<() => Promise<void>> {
    let eventResult: OfficeExtension.EventHandlerResult<Excel.WorksheetAddedEventArgs> | null = null;

    try {
      await Excel.run(async (context) => {
        const worksheets = context.workbook.worksheets;

        eventResult = worksheets.onAdded.add(async (event) => {
          try {
            callback(event.worksheetId);
          } catch (error) {
            console.error("Error in worksheet added handler:", error);
          }
        });

        await context.sync();
      });

      return async () => {
        if (eventResult) {
          await Excel.run(async (context) => {
            eventResult!.remove();
            await context.sync();
          });
        }
      };
    } catch (error) {
      console.error("Error registering worksheet added handler:", error);
      throw new Error(`Failed to register worksheet added handler: ${error}`);
    }
  }

  /**
   * Register a callback for when a worksheet is deleted from the workbook
   * @param callback - Function to call when a worksheet is deleted, receives the worksheet ID
   * @returns A function to unregister the event handler
   */
  static async onWorksheetDeleted(
    callback: (worksheetId: string) => void
  ): Promise<() => Promise<void>> {
    let eventResult: OfficeExtension.EventHandlerResult<Excel.WorksheetDeletedEventArgs> | null = null;

    try {
      await Excel.run(async (context) => {
        const worksheets = context.workbook.worksheets;

        eventResult = worksheets.onDeleted.add(async (event) => {
          try {
            callback(event.worksheetId);
          } catch (error) {
            console.error("Error in worksheet deleted handler:", error);
          }
        });

        await context.sync();
      });

      return async () => {
        if (eventResult) {
          await Excel.run(async (context) => {
            eventResult!.remove();
            await context.sync();
          });
        }
      };
    } catch (error) {
      console.error("Error registering worksheet deleted handler:", error);
      throw new Error(`Failed to register worksheet deleted handler: ${error}`);
    }
  }

  /**
   * Register a callback for when a worksheet is renamed in the workbook.
   * Requires ExcelApi 1.17 - returns null if not supported.
   * @param callback - Function to call when a worksheet is renamed
   * @returns A function to unregister the event handler, or null if not supported
   */
  static async onWorksheetNameChanged(
    callback: (worksheetId: string, nameAfter: string, nameBefore: string) => void
  ): Promise<(() => Promise<void>) | null> {
    if (!Office.context.requirements.isSetSupported("ExcelApi", "1.17")) {
      console.warn("ExcelApi 1.17 not supported - worksheet rename detection unavailable");
      return null;
    }

    let eventResult: OfficeExtension.EventHandlerResult<Excel.WorksheetNameChangedEventArgs> | null = null;

    try {
      await Excel.run(async (context) => {
        const worksheets = context.workbook.worksheets;

        eventResult = worksheets.onNameChanged.add(async (event) => {
          try {
            callback(event.worksheetId, event.nameAfter, event.nameBefore);
          } catch (error) {
            console.error("Error in worksheet name changed handler:", error);
          }
        });

        await context.sync();
      });

      return async () => {
        if (eventResult) {
          await Excel.run(async (context) => {
            eventResult!.remove();
            await context.sync();
          });
        }
      };
    } catch (error) {
      console.error("Error registering worksheet name changed handler:", error);
      return null;
    }
  }

  /**
   * Register callbacks for worksheet structural changes (added, deleted, renamed).
   * Registers all available event handlers and returns a single cleanup function.
   * @param onChanged - Function to call when any worksheet change occurs
   * @returns A function to unregister all event handlers
   */
  static async onWorksheetCollectionChanged(
    onChanged: () => void
  ): Promise<() => Promise<void>> {
    const unsubscribers: (() => Promise<void>)[] = [];

    try {
      const unsubAdded = await ExcelService.onWorksheetAdded(() => onChanged());
      unsubscribers.push(unsubAdded);

      const unsubDeleted = await ExcelService.onWorksheetDeleted(() => onChanged());
      unsubscribers.push(unsubDeleted);

      const unsubRenamed = await ExcelService.onWorksheetNameChanged(() => onChanged());
      if (unsubRenamed) {
        unsubscribers.push(unsubRenamed);
      }
    } catch (error) {
      for (const unsub of unsubscribers) {
        try { await unsub(); } catch { /* ignore cleanup errors */ }
      }
      throw error;
    }

    return async () => {
      for (const unsub of unsubscribers) {
        try {
          await unsub();
        } catch (error) {
          console.error("Error unregistering worksheet change handler:", error);
        }
      }
    };
  }

  /**
   * Set the tab colour for a worksheet
   * @param worksheetName - The name of the worksheet
   * @param color - The hex colour to set (e.g., "#60A5FA")
   */
  static async setWorksheetTabColor(worksheetName: string, color: string): Promise<void> {
    try {
      await Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getItem(worksheetName);
        worksheet.tabColor = color;
        await context.sync();
      });
    } catch (error) {
      console.error(`Error setting tab color for worksheet "${worksheetName}":`, error);
      // Don't throw - tab colour is a nice-to-have, not critical
    }
  }

  /**
   * Create a Completion Certificate worksheet with sign-off details.
   * The sheet is protected so users cannot edit it.
   * @param info - Sign-off information to display on the certificate
   */
  static async createCompletionCertificate(info: {
    // Both signing pairs are optional so this cert can be regenerated at
    // ANY point in the sign-off flow: manager signs first → cert shows
    // just the manager block so the partner can see review work has
    // happened; partner signs later → cert is rewritten with both blocks.
    // Caller must supply at least one signed pair — an assertion at the
    // top of the run enforces it so we never emit an empty certificate.
    managerEmail?: string;
    managerSignedTime?: string;
    partnerEmail?: string;
    partnerSignedTime?: string;
    // Optional co-signature — a second partner endorsing the same
    // sign-off. Rendered as its own block on the cert when both fields
    // are provided; omitted otherwise.
    secondaryPartnerEmail?: string;
    secondaryPartnerSignedTime?: string;
    managerChecklist?: { text: string; response: string }[];
    partnerChecklist?: { text: string; response: string }[];
    statusItems?: {
      name: string;
      status: string;
      lastUpdated: string;
      updatedBy: string;
    }[];
    reviewPoints?: {
      title: string;
      status: string;
      snapshot: string;
    }[];
  }): Promise<void> {
    const managerSigned = !!(info.managerEmail && info.managerSignedTime);
    const partnerSigned = !!(info.partnerEmail && info.partnerSignedTime);
    if (!managerSigned && !partnerSigned) {
      // Nothing to attest to — a manager-only cert is fine, a partner-only
      // cert is fine, but neither would be a blank sheet. Bail before we
      // touch the workbook so we don't leave an empty Completion
      // Certificate tab behind.
      console.warn("createCompletionCertificate skipped — neither manager nor partner is signed.");
      return;
    }
    const sheetName = COMPLETION_CERTIFICATE_SHEET_NAME;

    // Pre-flight: refuse up front rather than letting Excel improvise.
    // Writing to a read-only workbook can fork it into a separate copy
    // (desktop Save As, or Excel-for-web's "editing copy"), landing the
    // certificate in a workbook the user never opened. Callers should
    // have already run this same guard BEFORE committing the sign-off
    // server-side; this is the belt-and-braces layer for any path that
    // reaches the writer directly.
    await ExcelService.assertWorkbookWritable("the completion certificate");

    try {
      await Excel.run(async (context) => {
        const sheets = context.workbook.worksheets;
        let sheet: Excel.Worksheet;

        // Reuse the existing sheet if present (e.g. re-signing), else add
        // one. getItemOrNullObject rather than getItem + catch: getItem
        // throws on sync, and a blanket catch can't tell "sheet doesn't
        // exist" from "sheet exists but is password-protected / the
        // workbook is read-only". The old shape fell into the catch in
        // those cases and then tried to add() a sheet whose name was
        // already taken, failing a second time with a confusing error.
        // (Office.js also treats a context as unusable after a failed
        // sync, so the old catch block was batching onto a dead context.)
        const existing = sheets.getItemOrNullObject(sheetName);
        existing.load("isNullObject");
        await context.sync();

        if (existing.isNullObject) {
          sheet = sheets.add(sheetName);
          await context.sync();
        } else {
          sheet = existing;
          sheet.visibility = Excel.SheetVisibility.visible;
          // Unprotect can legitimately fail (sheet protected with a
          // password we don't have). Surface that as its own message
          // instead of silently falling through to a name collision.
          try {
            sheet.protection.unprotect();
            sheet.getRange().clear(Excel.ClearApplyTo.all);
            await context.sync();
          } catch (protectionError) {
            console.error("Could not clear the existing certificate sheet:", protectionError);
            throw new Error(
              `The existing "${sheetName}" sheet is protected and couldn't be cleared. ` +
                `Unprotect or delete that sheet, then sign off again.`,
            );
          }
        }

        // Move to first position (leftmost tab). Non-fatal: setting
        // position throws when the workbook STRUCTURE is protected, and a
        // certificate sitting in the wrong tab order is much better than
        // aborting the write half-way and leaving a blank sheet behind.
        try {
          sheet.position = 0;
          await context.sync();
        } catch (positionError) {
          console.warn(
            "Could not move the certificate sheet to the first tab position " +
              "(workbook structure may be protected):",
            positionError,
          );
        }

        // Format times in NZ timezone
        const nzOptions: Intl.DateTimeFormatOptions = {
          timeZone: "Pacific/Auckland",
          dateStyle: "full",
          timeStyle: "medium",
        };

        const managerTime = info.managerSignedTime
          ? new Date(info.managerSignedTime).toLocaleString("en-NZ", nzOptions)
          : "Not yet signed";

        const partnerTime = info.partnerSignedTime
          ? new Date(info.partnerSignedTime).toLocaleString("en-NZ", nzOptions)
          : "Not yet signed";

        // Intro line reflects the actual state — reads accurately whether
        // the cert is being regenerated after a manager sign (partner still
        // to come) or a partner sign (terminal).
        const introLine = partnerSigned
          ? "This workbook has been reviewed and signed off."
          : "This workbook has been reviewed by the manager. Partner sign-off is still pending.";

        // Build certificate content — sign-off section (columns A+B). Row
        // numbers are computed as we go rather than hard-coded so the
        // Manager / Partner blocks can be included conditionally.
        const data: (string | number)[][] = [
          ["Completion Certificate", "", "", ""],
          ["", "", "", ""],
          [introLine, "", "", ""],
          ["", "", "", ""],
        ];

        const sectionHeaderRows: number[] = [];
        const boldLabelRows: number[] = [];
        const tableSections: { headerRow: number; dataCount: number; cols: string }[] = [];

        // Manager Sign-off block — rendered whenever the manager has signed.
        if (managerSigned) {
          const headerRow = data.length + 1;
          data.push(["Manager Sign-off", "", "", ""]);
          sectionHeaderRows.push(headerRow);
          boldLabelRows.push(data.length + 1);
          data.push(["Signed By", info.managerEmail!, "", ""]);
          boldLabelRows.push(data.length + 1);
          data.push(["Signed At", managerTime, "", ""]);
        }

        // Partner Sign-off block — only when the partner has signed.
        if (partnerSigned) {
          if (managerSigned) data.push(["", "", "", ""]);
          const headerRow = data.length + 1;
          data.push(["Partner Sign-off", "", "", ""]);
          sectionHeaderRows.push(headerRow);
          boldLabelRows.push(data.length + 1);
          data.push(["Signed By", info.partnerEmail!, "", ""]);
          boldLabelRows.push(data.length + 1);
          data.push(["Signed At", partnerTime, "", ""]);
        }

        // Secondary Partner Sign-off — a co-signature block, rendered only
        // when the workbook has been secondary-signed. Row numbers are
        // computed from the current data length so the block slots in
        // cleanly ahead of any downstream checklist / status-items sections.
        if (info.secondaryPartnerEmail && info.secondaryPartnerSignedTime) {
          const secondaryTime = new Date(info.secondaryPartnerSignedTime).toLocaleString("en-NZ", nzOptions);
          data.push(["", "", "", ""]);
          const headerRow = data.length + 1;
          data.push(["Secondary Partner Sign-off", "", "", ""]);
          sectionHeaderRows.push(headerRow);
          const signedByRow = data.length + 1;
          data.push(["Signed By", info.secondaryPartnerEmail, "", ""]);
          boldLabelRows.push(signedByRow);
          const signedAtRow = data.length + 1;
          data.push(["Signed At", secondaryTime, "", ""]);
          boldLabelRows.push(signedAtRow);
        }

        // Helper to add a checklist section
        const addChecklistSection = (
          title: string,
          items: { text: string; response: string }[]
        ) => {
          data.push(["", "", "", ""]);
          const headerRow = data.length + 1; // 1-indexed Excel row
          data.push([title, "", "", ""]);
          sectionHeaderRows.push(headerRow);

          const tableHeaderRow = data.length + 1;
          data.push(["Question", "Response", "", ""]);
          for (const item of items) {
            data.push([item.text, item.response === "yes" ? "Yes" : "No", "", ""]);
          }
          tableSections.push({ headerRow: tableHeaderRow, dataCount: items.length, cols: "B" });
        };

        // Add checklist sections
        const managerChecklist = info.managerChecklist ?? [];
        if (managerChecklist.length > 0) {
          addChecklistSection("Manager Checklist", managerChecklist);
        }

        const partnerChecklist = info.partnerChecklist ?? [];
        if (partnerChecklist.length > 0) {
          addChecklistSection("Partner Checklist", partnerChecklist);
        }

        // Add status items section if provided
        const statusItems = info.statusItems ?? [];
        if (statusItems.length > 0) {
          data.push(["", "", "", ""]);
          const headerRow = data.length + 1;
          data.push(["Status Items Summary", "", "", ""]);
          sectionHeaderRows.push(headerRow);

          const tableHeaderRow = data.length + 1;
          data.push(["Name", "Status", "Last Updated", "Updated By"]);

          for (const item of statusItems) {
            const updatedTime = item.lastUpdated
              ? new Date(item.lastUpdated).toLocaleString("en-NZ", nzOptions)
              : "";
            data.push([item.name, item.status || "Open", updatedTime, item.updatedBy]);
          }
          tableSections.push({ headerRow: tableHeaderRow, dataCount: statusItems.length, cols: "D" });
        }

        // Add review points section if provided
        const reviewPoints = info.reviewPoints ?? [];
        if (reviewPoints.length > 0) {
          data.push(["", "", "", ""]);
          const headerRow = data.length + 1;
          data.push(["Open Review Points at Sign-off", "", "", ""]);
          sectionHeaderRows.push(headerRow);

          const tableHeaderRow = data.length + 1;
          data.push(["Review Point", "Status", "Captured At", ""]);

          for (const rp of reviewPoints) {
            data.push([rp.title, rp.status, rp.snapshot, ""]);
          }
          tableSections.push({ headerRow: tableHeaderRow, dataCount: reviewPoints.length, cols: "C" });
        }

        // Write data (4 columns: A-D)
        const range = sheet.getRange("A1").getResizedRange(data.length - 1, 3);
        range.values = data;

        // Format title row
        const titleRange = sheet.getRange("A1");
        titleRange.format.font.bold = true;
        titleRange.format.font.size = 16;

        // Format section headers
        for (const row of sectionHeaderRows) {
          const headerRange = sheet.getRange(`A${row}`);
          headerRange.format.font.bold = true;
          headerRange.format.font.size = 12;
        }

        // Format labels column for sign-off rows
        for (const row of boldLabelRows) {
          const labelRange = sheet.getRange(`A${row}`);
          labelRange.format.font.bold = true;
        }

        // Format table sections (checklists + status items)
        for (const section of tableSections) {
          // Table header row — bold with gray background
          const thRange = sheet.getRange(`A${section.headerRow}:${section.cols}${section.headerRow}`);
          thRange.format.font.bold = true;
          thRange.format.fill.color = "#E5E7EB";

          // Borders around the table
          const tableEndRow = section.headerRow + section.dataCount;
          const tableRange = sheet.getRange(`A${section.headerRow}:${section.cols}${tableEndRow}`);
          tableRange.format.borders.getItem("InsideHorizontal").style = "Continuous";
          tableRange.format.borders.getItem("InsideVertical").style = "Continuous";
          tableRange.format.borders.getItem("EdgeTop").style = "Continuous";
          tableRange.format.borders.getItem("EdgeBottom").style = "Continuous";
          tableRange.format.borders.getItem("EdgeLeft").style = "Continuous";
          tableRange.format.borders.getItem("EdgeRight").style = "Continuous";
        }

        // Auto-fit columns then cap A so long checklist text wraps
        sheet.getRange("A:D").format.autofitColumns();
        sheet.getRange("A:A").format.columnWidth = 400;
        sheet.getRange("A:A").format.wrapText = true;

        // Set tab color to green (matches "Partner Reviewed" / success)
        sheet.tabColor = "#009966";

        // Protect the sheet so users cannot edit it
        sheet.protection.protect({
          allowAutoFilter: false,
          allowDeleteColumns: false,
          allowDeleteRows: false,
          allowEditObjects: false,
          allowEditScenarios: false,
          allowFormatCells: false,
          allowFormatColumns: false,
          allowFormatRows: false,
          allowInsertColumns: false,
          allowInsertHyperlinks: false,
          allowInsertRows: false,
          allowPivotTables: false,
          allowSort: false,
        });

        await context.sync();
      });
    } catch (error) {
      console.error("Error creating completion certificate:", error);
      // Preserve our own typed / already-actionable errors instead of
      // burying their message inside a generic wrapper.
      if (
        error instanceof WorkbookReadOnlyError ||
        error instanceof WorkbookNotSavedError ||
        error instanceof WorkbookSaveError
      ) {
        throw error;
      }
      throw new Error(`Failed to create completion certificate: ${error}`);
    }
  }
}
